import { apiClient } from './client.js';
import { API_ENDPOINTS } from '../config/constants.js';
import type { DonationForm, DonationResponse } from '../models/donation.js';

interface DonationFormApiResponse extends Omit<DonationForm, 'show_why_you_support_prompt' | 'theme_settings'> {
  theme_settings?: {
    show_why_you_support_prompt?: boolean;
  } | null;
}

function normalizeDonationFormResponse(response: DonationFormApiResponse): DonationForm {
  const show_why_you_support_prompt = response.theme_settings?.show_why_you_support_prompt ?? true;
  const { theme_settings, ...rest } = response;
  return {
    ...rest,
    theme_settings: theme_settings ?? null,
    show_why_you_support_prompt,
  };
}

export async function getDonationForm(formId: string): Promise<DonationForm> {
  const response = await apiClient.get<DonationFormApiResponse>(
    `${API_ENDPOINTS.DONATION_FORMS}/${formId}`
  );
  
  if (response) {
    return normalizeDonationFormResponse(response);
  }
  
  throw new Error('Failed to get donation form');
}

export async function getDonationFormBySlug(
  formSlug: string,
  options?: { signal?: AbortSignal; preview?: boolean }
): Promise<DonationForm> {
  const query = options?.preview ? '?preview=true' : '';
  const response = await apiClient.get<DonationFormApiResponse>(
    `${API_ENDPOINTS.DONATION_FORMS}/slug/${encodeURIComponent(formSlug)}${query}`,
    { signal: options?.signal },
  );

  if (response) {
    return normalizeDonationFormResponse(response);
  }

  throw new Error('Failed to get donation form');
}

export async function getDonationReceipt(
  receiptNumber: string,
  options?: { updateToken?: string; withAuth?: boolean; signal?: AbortSignal }
): Promise<DonationResponse> {
  const query = options?.updateToken ? `?update_token=${encodeURIComponent(options.updateToken)}` : '';
  return await apiClient.get<DonationResponse>(
    `${API_ENDPOINTS.DONATIONS}/${encodeURIComponent(receiptNumber)}${query}`,
    { withAuth: options?.withAuth, signal: options?.signal },
  );
}

export async function getDefaultDonationForm(options?: { signal?: AbortSignal; preview?: boolean }): Promise<DonationForm> {
  const query = options?.preview ? '?preview=true' : '';
  const response = await apiClient.get<DonationFormApiResponse>(
    `${API_ENDPOINTS.DONATION_FORMS}/default${query}`,
    { signal: options?.signal },
  );

  if (response) {
    return normalizeDonationFormResponse(response);
  }

  throw new Error('Failed to get default donation form');
}

export async function getActiveSubscription(jwt: string): Promise<DonationResponse> {
  const token = encodeURIComponent(jwt);
  const response = await apiClient.get<DonationResponse>(
    `/donate/v1/donations/active_subscription?token=${token}`
  );

  if (response) {
    return response;
  }

  throw new Error('Failed to get active subscription');
}